/**
 * Flowlytics Pixel SDK - Consent Management Integration
 *
 * Two-layer consent:
 * 1. Google Consent Mode v2 (analytics_storage, ad_storage, etc.)
 * 2. GDPR category consent (analytics, marketing, functional)
 *
 * Enforces tracking/CAPI gating based on consent state.
 * Persists consent to localStorage. The decision reaches the backend only as a
 * `consent_update` event (recorded in consent_decisions): there is no separate
 * sync — /api/v1/consent/visitor was removed on 2026-09-11 because it wrote the
 * visitor's consent onto the installation OWNER.
 *
 * Updated: 2026-02-08 (Phase 1, Week 3 — GDPR Consent & PII Encryption)
 */

const CONSENT_STORAGE_KEY = "flw_consent";
const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000; // 6 months

// How long to keep trying to read the purposes Iubenda just saved before
// treating the decision as a denial (~3s in total). See _applyIubendaDecision.
const IUBENDA_READ_RETRY_DELAYS_MS = [50, 100, 200, 400, 800, 1600];

class ConsentIntegration {
  constructor(config, flowlytics) {
    this.config = config;
    this.flowlytics = flowlytics;

    // Google Consent Mode v2 state
    this.consentState = {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    };

    // GDPR category consent (maps to backend consent types)
    this.gdprConsent = {
      analytics: false, // -> analytics, behavioral_tracking
      marketing: false, // -> marketing_communications, third_party_sharing, retargeting
      functional: true, // -> personalization (always allowed by default)
    };

    this._lastStateHash = this._hashState(this.consentState);
    this._consentDecided = false;

    // Bumped by every Iubenda decision: a read still retrying for an older
    // decision sees a different token and gives up instead of overwriting.
    this._iubendaReadToken = 0;
  }

  /**
   * Initialize consent mode
   */
  init() {
    // Restore saved consent from localStorage
    this._restoreConsent();

    // Set Google Consent Mode v2 defaults
    if (typeof window.gtag === "function") {
      this.initGoogleConsentMode();
    }

    // Auto-detect and wire Iubenda before any other listeners
    this._initIubenda();

    // Listen for custom consent events (manual integration)
    window.addEventListener("flowlytics_consent_update", (event) => {
      this.handleConsentUpdate(event.detail);
    });

    // Listen for GDPR consent banner events
    window.addEventListener("flowlytics_gdpr_consent", (event) => {
      this.updateGDPRConsent(event.detail);
    });

    if (this.config.debug) {
      console.log(
        "[Flowlytics Consent] Integration initialized, decided:",
        this._consentDecided,
        "iubenda:",
        this._iubendaDetected,
      );
    }
  }

  /**
   * Auto-detect Iubenda and wire consent events.
   * Reads existing consent on first call; afterwards listens for
   * iubenda_consent_given / iubenda_cookie_policy_rejected events.
   */
  _initIubenda() {
    this._iubendaDetected = false;

    if (!this.isIubendaPresent()) return;

    this._iubendaDetected = true;

    // If user already decided in a previous session → read and apply immediately
    const existing = this._readIubendaConsent();
    if (existing) {
      if (this.config.debug) {
        console.log(
          "[Flowlytics Consent] Iubenda: existing consent →",
          existing,
        );
      }
      this._applyIubendaConsent(existing);
      return;
    }

    // User hasn't decided yet — listen for Iubenda events.
    // Fires when the user saves ANY choice, "necessary only" included: it says
    // that a decision exists, not what it is. What was granted is read from
    // the purposes (see _applyIubendaDecision).
    window.addEventListener("iubenda_consent_given", () => {
      this._applyIubendaDecision();
    });

    // Fires when user clicks "Reject all"
    window.addEventListener("iubenda_cookie_policy_rejected", () => {
      this._iubendaReadToken++; // supersedes a decision still being read
      if (this.config.debug) {
        console.log("[Flowlytics Consent] Iubenda: consent rejected");
      }
      this._applyIubendaConsent({
        analytics: false,
        marketing: false,
        functional: true,
      });
    });

    // Fires when consent is not required in the current jurisdiction
    window.addEventListener("iubenda_preference_not_needed", () => {
      this._iubendaReadToken++; // supersedes a decision still being read
      if (this.config.debug) {
        console.log("[Flowlytics Consent] Iubenda: consent not needed");
      }
      this._applyIubendaConsent({
        analytics: true,
        marketing: true,
        functional: true,
      });
    });
  }

  /**
   * Read current consent state from the Iubenda CS API.
   * Returns null if Iubenda API is not ready or consent not given yet.
   */
  _readIubendaConsent() {
    try {
      const api = window._iub?.cs?.api;
      if (!api || typeof api.isConsentGiven !== "function") return null;
      if (!api.isConsentGiven()) return null;

      return this._mapIubendaPurposes(api.consent?.purposes || {});
    } catch (_) {
      return null;
    }
  }

  /**
   * Read the purposes Iubenda holds right now, whatever isConsentGiven() says.
   * Returns null only when there is nothing to read yet.
   */
  _readIubendaPurposes() {
    try {
      const purposes = window._iub?.cs?.api?.consent?.purposes;
      if (!purposes || typeof purposes !== "object") return null;
      return this._mapIubendaPurposes(purposes);
    } catch (_) {
      return null;
    }
  }

  /**
   * Maps Iubenda purpose IDs to Flowlitycs categories:
   *   purpose 4 (Measurement) → analytics
   *   purpose 5 (Targeting/Marketing) → marketing
   */
  _mapIubendaPurposes(purposes) {
    return {
      analytics: !!(purposes[4] || purposes["4"]),
      marketing: !!(purposes[5] || purposes["5"]),
      functional: true,
    };
  }

  /**
   * Apply the decision the visitor just saved in Iubenda, read from its purposes.
   *
   * ⚠️ NEVER assume "granted" when the purposes can't be read. Until 2026-09-10
   * this path did `_readIubendaConsent() || { analytics: true, marketing: true }`.
   * Measured in production on 7 days of raw events: in 49 of the 53 sessions
   * where a fingerprint reached the backend with analytics=false, the visitor
   * had granted ONLY Iubenda purpose 1 (necessary) and never purpose 4
   * (measurement) — yet the SDK sent page_view and the next events with
   * analytics=true and a fingerprint, and corrected itself only afterwards. The
   * fingerprint had already left the browser (ePrivacy art. 5(3)).
   * `isConsentGiven()` is not the question here either: it says consent exists,
   * not for which purposes, so this path reads the purposes directly.
   *
   * Unreadable → retry with a growing delay; still unreadable → deny. A visitor
   * who consented loses analytics for this one page; a visitor who refused is
   * never fingerprinted. The error lands on the side that can be recovered.
   */
  _applyIubendaDecision() {
    const token = ++this._iubendaReadToken;

    const attempt = (n) => {
      if (token !== this._iubendaReadToken) return; // a newer decision won

      const consent = this._readIubendaPurposes();
      if (consent) {
        if (this.config.debug) {
          console.log("[Flowlytics Consent] Iubenda: consent given →", consent);
        }
        this._applyIubendaConsent(consent);
        return;
      }

      if (n < IUBENDA_READ_RETRY_DELAYS_MS.length) {
        setTimeout(() => attempt(n + 1), IUBENDA_READ_RETRY_DELAYS_MS[n]);
        return;
      }

      if (this.config.debug) {
        console.warn(
          "[Flowlytics Consent] Iubenda: purposes unreadable, treating as denied",
        );
      }
      this._applyIubendaConsent({
        analytics: false,
        marketing: false,
        functional: true,
      });
    };

    attempt(0);
  }

  /**
   * Apply Iubenda consent — delegates to updateGDPRConsent which handles
   * Google Consent Mode sync, localStorage persistence and queue flush.
   */
  _applyIubendaConsent(consent) {
    this.updateGDPRConsent(consent);
  }

  /**
   * Whether Iubenda was detected on this page.
   */
  isIubendaDetected() {
    return !!this._iubendaDetected;
  }

  /**
   * Whether Iubenda is on the page RIGHT NOW. isIubendaDetected() is fixed at
   * init; this looks again, because GTM can inject Iubenda after the SDK starts.
   */
  isIubendaPresent() {
    // Iubenda is present if its global object exists OR its script is in the DOM
    return (
      typeof window._iub !== "undefined" ||
      !!document.querySelector('script[src*="iubenda"], script[src*="iubnda"]')
    );
  }

  /**
   * Initialize Google Consent Mode v2
   */
  initGoogleConsentMode() {
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });

    if (this.config.debug) {
      console.log("[Flowlytics Consent] Google Consent Mode v2 initialized");
    }
  }

  /**
   * Handle consent state update (Google Consent Mode v2).
   * Called by GTM integration when it detects consent array pushes,
   * or by the flowlytics_consent_update custom event listener.
   * Only fires a tracking event if state actually changed.
   */
  handleConsentUpdate(newState) {
    if (!newState || typeof newState !== "object") return;

    const merged = { ...this.consentState, ...newState };
    const newHash = this._hashState(merged);

    // Only fire event if state actually changed
    if (newHash === this._lastStateHash) {
      if (this.config.debug) {
        console.log(
          "[Flowlytics Consent] No change, skipping duplicate update",
        );
      }
      return;
    }

    this.consentState = merged;
    this._lastStateHash = newHash;

    // Sync GDPR state from Google Consent Mode categories
    this.gdprConsent.analytics = merged.analytics_storage === "granted";
    this.gdprConsent.marketing = merged.ad_storage === "granted";
    this._consentDecided = true;
    this._persistConsent();
    this._forgetAnalyticsDataIfDenied();

    // Track the actual consent change (always allowed — it's a consent event)
    // Copies: see updateGDPRConsent — the queue serializes after later mutations.
    this.flowlytics.track("consent_update", {
      consent_state: { ...this.consentState },
      gdpr_consent: { ...this.gdprConsent },
      timestamp: new Date().toISOString(),
    });

    // S7: Flush pre-consent queue after consent decision
    if (this.flowlytics._flushPreConsentQueue) {
      this.flowlytics._flushPreConsentQueue();
    }

    if (this.config.debug) {
      console.log("[Flowlytics Consent] State updated:", this.consentState);
    }
  }

  /**
   * Update GDPR category consent (from consent banner).
   * @param {Object} consent - { analytics: bool, marketing: bool, functional: bool }
   */
  updateGDPRConsent(consent) {
    if (!consent || typeof consent !== "object") return;

    // Sync Google Consent Mode v2 from GDPR categories
    const gCM = {
      analytics_storage: consent.analytics === true ? "granted" : "denied",
      ad_storage: consent.marketing === true ? "granted" : "denied",
      ad_user_data: consent.marketing === true ? "granted" : "denied",
      ad_personalization: consent.marketing === true ? "granted" : "denied",
    };

    // Skip if consent state hasn't actually changed
    const newHash = this._hashState(gCM);
    if (newHash === this._lastStateHash && this._consentDecided) {
      if (this.config.debug) {
        console.log("[Flowlytics Consent] GDPR no change, skipping duplicate");
      }
      return;
    }

    this.gdprConsent = {
      analytics: consent.analytics === true,
      marketing: consent.marketing === true,
      functional: consent.functional !== false,
    };
    this._consentDecided = true;
    this.consentState = gCM;
    this._lastStateHash = newHash;
    this._forgetAnalyticsDataIfDenied();

    // Update Google gtag if available
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", gCM);
    }

    this._persistConsent();

    // Track consent change. COPIES, not the live objects: the queue serializes
    // later, and handleConsentUpdate mutates this.gdprConsent in place — in prod
    // 27 of 888 consent_update events carried a gdpr_consent that never
    // coexisted with their own consent_state.
    this.flowlytics.track("consent_update", {
      consent_state: { ...this.consentState },
      gdpr_consent: { ...this.gdprConsent },
      source: "gdpr_banner",
      timestamp: new Date().toISOString(),
    });

    // S7: Flush pre-consent queue after consent decision
    if (this.flowlytics._flushPreConsentQueue) {
      this.flowlytics._flushPreConsentQueue();
    }

    if (this.config.debug) {
      console.log(
        "[Flowlytics Consent] GDPR consent updated:",
        this.gdprConsent,
      );
    }
  }

  /**
   * Without measurement consent, forget what was collected under an earlier
   * state. The SDK caches fingerprint, device and cookie ids once per page and
   * light events reuse them: until 2026-09-10 the cache outlived the decision
   * and kept going out (107 events at analytics=false in 7 days of production).
   * Runs BEFORE the consent_update is tracked, which is itself a light event.
   */
  _forgetAnalyticsDataIfDenied() {
    if (this.isAnalyticsAllowed()) return;
    if (typeof this.flowlytics._dropAnalyticsGatedCaches === "function") {
      this.flowlytics._dropAnalyticsGatedCaches();
    }
  }

  /**
   * Hash consent state for comparison
   */
  _hashState(state) {
    return Object.keys(state)
      .sort()
      .map((k) => `${k}:${state[k]}`)
      .join("|");
  }

  /**
   * Persist consent to localStorage
   */
  _persistConsent() {
    try {
      localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify({
          gdpr: this.gdprConsent,
          gcm: this.consentState,
          decided: this._consentDecided,
          ts: Date.now(),
        }),
      );
    } catch (e) {
      // localStorage may be unavailable
    }
  }

  /**
   * Restore consent from localStorage
   */
  _restoreConsent() {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);

      // Expire consent after 6 months — re-ask user
      if (saved.ts && Date.now() - saved.ts > CONSENT_MAX_AGE_MS) {
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        if (this.config.debug) {
          console.log("[Flowlytics Consent] Consent expired, will re-ask");
        }
        return;
      }

      if (saved.gdpr) {
        this.gdprConsent = saved.gdpr;
      }
      if (saved.gcm) {
        this.consentState = saved.gcm;
        this._lastStateHash = this._hashState(saved.gcm);
      }
      if (saved.decided) {
        this._consentDecided = true;
      }

      if (this.config.debug) {
        console.log(
          "[Flowlytics Consent] Restored from localStorage:",
          this.gdprConsent,
        );
      }
    } catch (e) {
      // Corrupted or unavailable
    }
  }

  /**
   * Whether user has made a consent decision (banner interaction)
   */
  hasDecided() {
    return this._consentDecided;
  }

  /**
   * Check if analytics tracking is allowed
   */
  isAnalyticsAllowed() {
    // S7: Block tracking until consent is explicitly given (GDPR compliant)
    if (!this._consentDecided) return false;
    return this.gdprConsent.analytics === true;
  }

  /**
   * Check if advertising/marketing is allowed (CAPI forwarding)
   */
  isAdvertisingAllowed() {
    if (!this._consentDecided) return false;
    return this.gdprConsent.marketing === true;
  }

  /**
   * Check if personalization is allowed
   */
  isPersonalizationAllowed() {
    return this.gdprConsent.functional !== false;
  }

  /**
   * Get current consent state
   */
  getConsentState() {
    return { ...this.consentState };
  }

  /**
   * Get GDPR consent state
   */
  getGDPRConsent() {
    return { ...this.gdprConsent };
  }

  /**
   * Grant all consent
   */
  grantAll() {
    this.updateGDPRConsent({
      analytics: true,
      marketing: true,
      functional: true,
    });
  }

  /**
   * Deny all consent (except functional)
   */
  denyAll() {
    this.updateGDPRConsent({
      analytics: false,
      marketing: false,
      functional: true,
    });
  }
}

export default ConsentIntegration;
